import { motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";

/**
 * LiveDashboardMock — animated AOCEMS dashboard preview.
 * Three KPIs drift continuously, the Emission Risk Analytics chart
 * jitters subtly and an active marker walks through months,
 * giving the impression of a live monitoring console.
 */
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const BASE_HIGH = [220, 245, 270, 320, 360, 340, 280, 245, 280, 360, 340, 305];
const BASE_LOW = [165, 180, 195, 215, 220, 200, 185, 175, 200, 230, 245, 235];

function clamp(v, min, max) {
  return Math.max(min, Math.min(max, v));
}

function KPICard({ icon, title, value, delta, deltaPositive = true, subtitle }) {
  return (
    <div className="bg-white rounded-2xl border border-outline-variant p-3 md:p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="w-9 h-9 rounded-lg bg-primary-container flex items-center justify-center">
          <span className="material-symbols-outlined text-white text-[18px]">{icon}</span>
        </div>
        <span className="material-symbols-outlined text-zinc-300 text-[18px]">more_horiz</span>
      </div>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[11px] text-on-surface-variant truncate">{title}</div>
          <div className="font-manrope font-extrabold text-primary text-xl md:text-2xl mt-0.5 tabular-nums leading-tight">
            {value}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0 pt-0.5">
          <span
            className={`font-mono text-[10px] font-bold px-1.5 py-0.5 rounded-md flex items-center gap-0.5 ${
              deltaPositive ? "text-blue-700 bg-blue-50" : "text-emerald-700 bg-emerald-50"
            }`}
          >
            <span className="material-symbols-outlined text-[12px]">trending_up</span>
            {delta}
          </span>
          <div className="text-[10px] text-on-surface-variant text-right leading-tight max-w-[90px]">
            {subtitle}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LiveDashboardMock() {
  // KPI values
  const [sites, setSites] = useState(340);
  const [sensors, setSensors] = useState(670);
  const [revenue, setRevenue] = useState(104575);
  const [sitesDelta, setSitesDelta] = useState(4.82);
  const [sensorsDelta, setSensorsDelta] = useState(2.73);
  const [revenueDelta, setRevenueDelta] = useState(5.7);

  // Chart series — start from base, drift continuously
  const [highSeries, setHighSeries] = useState(BASE_HIGH);
  const [lowSeries, setLowSeries] = useState(BASE_LOW);

  // Active month — auto-cycles to mimic live monitoring
  const [activeIdx, setActiveIdx] = useState(4); // May

  // KPI + chart drift
  useEffect(() => {
    const id = setInterval(() => {
      setSites((s) => clamp(s + (Math.random() - 0.45) * 3, 332, 358));
      setSensors((s) => clamp(s + (Math.random() - 0.45) * 5, 642, 712));
      setRevenue((s) => clamp(s + (Math.random() - 0.45) * 900, 96000, 114000));
      setSitesDelta((d) => clamp(d + (Math.random() - 0.5) * 0.08, 4.2, 5.4));
      setSensorsDelta((d) => clamp(d + (Math.random() - 0.5) * 0.06, 2.2, 3.3));
      setRevenueDelta((d) => clamp(d + (Math.random() - 0.5) * 0.1, 4.8, 6.6));
      setHighSeries((prev, _) =>
        prev.map((v, i) => clamp(BASE_HIGH[i] + (v - BASE_HIGH[i]) * 0.7 + (Math.random() - 0.5) * 6, 200, 380))
      );
      setLowSeries((prev) =>
        prev.map((v, i) => clamp(BASE_LOW[i] + (v - BASE_LOW[i]) * 0.7 + (Math.random() - 0.5) * 4, 140, 260))
      );
    }, 1800);
    return () => clearInterval(id);
  }, []);

  // Walking tooltip
  useEffect(() => {
    const id = setInterval(() => {
      setActiveIdx((i) => (i + 1) % 12);
    }, 2600);
    return () => clearInterval(id);
  }, []);

  // SVG geometry
  const W = 760;
  const H = 280;
  const PAD_L = 38;
  const PAD_R = 18;
  const PAD_T = 12;
  const PAD_B = 32;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;
  const xAt = (i) => PAD_L + (i / 11) * innerW;
  const yAt = (v) => PAD_T + (1 - clamp(v, 0, 400) / 400) * innerH;

  // Smoothed cubic-bezier path (Catmull–Rom-like)
  const smooth = (pts) => {
    if (pts.length < 2) return "";
    let d = `M ${pts[0].x} ${pts[0].y}`;
    for (let i = 0; i < pts.length - 1; i++) {
      const p0 = pts[i - 1] || pts[i];
      const p1 = pts[i];
      const p2 = pts[i + 1];
      const p3 = pts[i + 2] || p2;
      const cp1x = p1.x + (p2.x - p0.x) / 6;
      const cp1y = p1.y + (p2.y - p0.y) / 6;
      const cp2x = p2.x - (p3.x - p1.x) / 6;
      const cp2y = p2.y - (p3.y - p1.y) / 6;
      d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
    }
    return d;
  };
  const ptsHigh = useMemo(
    () => highSeries.map((v, i) => ({ x: xAt(i), y: yAt(v) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [highSeries]
  );
  const ptsLow = useMemo(
    () => lowSeries.map((v, i) => ({ x: xAt(i), y: yAt(v) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lowSeries]
  );
  const highPath = smooth(ptsHigh);
  const lowPath = smooth(ptsLow);
  const highArea = `${highPath} L ${xAt(11)} ${yAt(0)} L ${xAt(0)} ${yAt(0)} Z`;
  const lowArea = `${lowPath} L ${xAt(11)} ${yAt(0)} L ${xAt(0)} ${yAt(0)} Z`;

  // Tooltip placement (anchor to top of upper curve)
  const tipLeftPct = (xAt(activeIdx) / W) * 100;
  const tipTopPct = (yAt(Math.max(highSeries[activeIdx], lowSeries[activeIdx])) / H) * 100;
  const tipFlipsLeft = activeIdx > 7;

  return (
    <div
      data-testid="live-dashboard-mock"
      className="relative bg-white rounded-3xl shadow-[0_30px_80px_-30px_rgba(16,26,20,0.25)] border border-outline-variant p-4 md:p-6"
    >
      {/* Live indicator */}
      <div className="absolute top-4 right-4 flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-mono text-brand font-bold z-10">
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full rounded-full bg-brand opacity-75 animate-ping" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-brand" />
        </span>
        Live
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        <KPICard
          icon="location_on"
          title="Monitoring Sites"
          value={Math.round(sites)}
          delta={`+${sitesDelta.toFixed(2)}%`}
          subtitle="Compliance Rates"
        />
        <KPICard
          icon="sensors"
          title="Active Sensors"
          value={Math.round(sensors)}
          delta={`+${sensorsDelta.toFixed(2)}%`}
          subtitle="Weekly Sensor Alerts"
        />
        <KPICard
          icon="payments"
          title="Revenue"
          value={`$${Math.round(revenue).toLocaleString()}`}
          delta={`${revenueDelta.toFixed(2)}%`}
          subtitle="from last week"
          deltaPositive={false}
        />
      </div>

      {/* Chart header */}
      <div className="flex items-start justify-between mb-1">
        <div>
          <h4 className="font-manrope font-bold text-primary text-base md:text-lg leading-tight">
            Emission Risk Analytics
          </h4>
          <p className="text-[11px] text-on-surface-variant">Carbon Concentration in PPM</p>
        </div>
        <span className="px-2.5 py-1.5 rounded-lg bg-surface-container-low border border-outline-variant text-[11px] font-bold text-primary flex items-center gap-1">
          This Year
          <span className="material-symbols-outlined text-[14px]">expand_more</span>
        </span>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mt-2 mb-1">
        <span className="flex items-center gap-1.5 text-[11px]">
          <span className="w-3 h-0.5 bg-brand rounded-full" />
          Low Risk
        </span>
        <span className="flex items-center gap-1.5 text-[11px]">
          <span className="w-3 h-0.5 bg-red-500 rounded-full" />
          High Risk
        </span>
      </div>

      {/* Chart */}
      <div className="relative">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto block" preserveAspectRatio="none">
          <defs>
            <linearGradient id="dash-high" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#ef4444" stopOpacity="0.18" />
              <stop offset="100%" stopColor="#ef4444" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="dash-low" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#90B849" stopOpacity="0.22" />
              <stop offset="100%" stopColor="#90B849" stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* Y grid */}
          {[0, 100, 200, 300, 400].map((v) => (
            <g key={v}>
              <line
                x1={PAD_L}
                x2={W - PAD_R}
                y1={yAt(v)}
                y2={yAt(v)}
                stroke="#e5e7eb"
                strokeWidth="1"
                strokeDasharray={v === 0 ? "0" : "0"}
              />
              <text
                x={PAD_L - 8}
                y={yAt(v) + 3}
                textAnchor="end"
                fontFamily="ui-monospace, monospace"
                fontSize="10"
                fill="#9ca3af"
              >
                {v}
              </text>
            </g>
          ))}

          {/* Areas */}
          <motion.path
            d={highArea}
            fill="url(#dash-high)"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
          />
          <motion.path
            d={lowArea}
            fill="url(#dash-low)"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          />

          {/* Lines */}
          <motion.path
            d={highPath}
            fill="none"
            stroke="#ef4444"
            strokeWidth="2.4"
            strokeLinejoin="round"
            strokeLinecap="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
          />
          <motion.path
            d={lowPath}
            fill="none"
            stroke="#90B849"
            strokeWidth="2.4"
            strokeLinejoin="round"
            strokeLinecap="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
          />

          {/* Active marker — static SVG (updated via state only) to avoid
              framer-motion's animated SVG-attribute interpolation producing
              transient NaN / undefined during route-level remounts. */}
          {(() => {
            const markerX = Number.isFinite(xAt(activeIdx)) ? xAt(activeIdx) : xAt(0);
            const markerY = Number.isFinite(yAt(highSeries[activeIdx]))
              ? yAt(highSeries[activeIdx])
              : yAt(BASE_HIGH[0]);
            return (
              <>
                <line
                  x1={markerX}
                  x2={markerX}
                  y1={PAD_T}
                  y2={H - PAD_B}
                  stroke="#374151"
                  strokeDasharray="3 3"
                  strokeWidth="1"
                />
                <circle
                  r="5"
                  fill="white"
                  stroke="#1f2937"
                  strokeWidth="2"
                  cx={markerX}
                  cy={markerY}
                  style={{ transition: "cx 0.5s ease, cy 0.5s ease" }}
                />
              </>
            );
          })()}

          {/* X labels */}
          {MONTHS.map((m, i) => (
            <text
              key={m}
              x={xAt(i)}
              y={H - PAD_B + 18}
              textAnchor="middle"
              fontSize="10"
              fill={i === activeIdx ? "#90B849" : "#6b7280"}
              fontWeight={i === activeIdx ? 700 : 400}
            >
              {m}
            </text>
          ))}
        </svg>

        {/* Floating tooltip */}
        <motion.div
          key={activeIdx}
          initial={{ opacity: 0, y: 6, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="absolute bg-white rounded-xl shadow-lg border border-outline-variant px-3 py-2.5 pointer-events-none z-10 min-w-[140px]"
          style={{
            left: `${tipLeftPct}%`,
            top: `${tipTopPct}%`,
            transform: tipFlipsLeft ? "translate(calc(-100% - 14px), -50%)" : "translate(14px, -50%)",
          }}
        >
          <div className="text-[11px] font-bold text-primary mb-1">{MONTHS[activeIdx]} 2025</div>
          <div className="flex items-center justify-between gap-3 mb-0.5">
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-0.5 bg-red-500 rounded-full" />
              <span className="text-[10px] text-on-surface-variant">High</span>
            </span>
            <span className="font-mono font-bold text-primary text-xs tabular-nums">
              {Math.round(highSeries[activeIdx])} PPM
            </span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-0.5 bg-brand rounded-full" />
              <span className="text-[10px] text-on-surface-variant">Low</span>
            </span>
            <span className="font-mono font-bold text-primary text-xs tabular-nums">
              {Math.round(lowSeries[activeIdx])} PPM
            </span>
          </div>
        </motion.div>

        {/* AOCEMS branding chip */}
        <div className="absolute bottom-2 left-2 flex items-center gap-2 px-3 py-2 rounded-xl bg-white shadow-md border border-outline-variant">
          <img src="/logos/aocems.svg" alt="AOCEMS" className="h-6 w-auto" />
        </div>
      </div>
    </div>
  );
}
